import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { TrendingUp, ShieldCheck, Sparkles, ChevronRight } from 'lucide-react';
import { formatINR } from '../data/products';

export default function GoldRateTicker() {
  const navigate = useNavigate();
  const { 
    liveGoldRates, 
    masterMetalRates, 
    announcementData,
    setIsWastageModalOpen,
    setIsWastageListOpen
  } = useShop();

  const announcements = announcementData?.items || [
    '💎 Pure BIS 916 Hallmarked Gold & Certified Solitaire Diamonds',
    '✨ Transparent Making Charges Linked to Daily Live Bullion Rates ✨',
    '👑 Regal Heritage Jewellery & Handcrafted Heavy Kitchenware Sets',
    '🛡️ 100% BIS Hallmarked HUID Gold • Insured Doorstep Delivery Across 19,000+ Pincodes'
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  // Cycle through announcements if multiple exist
  useEffect(() => {
    if (announcements.length <= 1) return;
    const intervalTime = announcementData?.marqueeSpeed === 'fast' ? 4000 : announcementData?.marqueeSpeed === 'slow' ? 8000 : 5500;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, intervalTime);
    return () => clearInterval(interval);
  }, [announcements.length, announcementData?.marqueeSpeed]);

  if (announcementData && announcementData.isEnabled === false) {
    return null;
  }

  const showRates = announcementData ? announcementData.showBullionTicker !== false : true;

  // Resolve rates dynamically from master rates or live rates fallback
  const rate24K = masterMetalRates?.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates?.gold24K || liveGoldRates?.gold24k || 7380;
  const rate22K = masterMetalRates?.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates?.gold22K || liveGoldRates?.gold22k || 6765;
  const rate18K = masterMetalRates?.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates?.gold18K || liveGoldRates?.gold18k || 5535;
  const rateSilver = masterMetalRates?.silver?.find((s) => s.label?.includes('999') || s.id?.includes('999'))?.rate || liveGoldRates?.silver999 || 89.5;

  return (
    <div className="gold-rate-ticker-ribbon" style={{
      background: '#1a5bb8',
      borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
      padding: '0.35rem 0',
      fontSize: '0.76rem',
      color: '#ffffff',
      position: 'relative',
      zIndex: 910
    }}>
      <div className="container">
        <div className="ticker-inner-wrap">
          {/* Left: Active Live Notice */}
          <div className="ticker-announcement-col">
            <span 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.1rem 0.5rem',
                borderRadius: '2px',
                background: '#ffe500',
                color: '#1a5bb8',
                fontSize: '0.65rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                flexShrink: 0
              }}
            >
              <Sparkles size={11} color="#1a5bb8" />
              M.L. PLUS
            </span>
            <span 
              className="ticker-notice-text"
              style={{ color: '#ffffff' }}
            >
              {announcements[currentIndex]}
            </span>
          </div>

          {/* Right: Live Bullion Rates */}
          {showRates && (
            <div className="ticker-rates-col">
              {/* 24K */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#76ff03',
                  boxShadow: '0 0 6px #76ff03',
                  flexShrink: 0
                }} />
                <span style={{ color: '#dbeafe' }}>24K:</span>
                <span style={{ color: '#ffe500', fontWeight: 700 }}>
                  {formatINR(rate24K)}/g
                </span>
              </div>

              {/* 22K */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <span style={{ color: '#dbeafe' }}>22K:</span>
                <span style={{ color: '#ffe500', fontWeight: 700 }}>
                  {formatINR(rate22K)}/g
                </span>
              </div>

              {/* 18K */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <span style={{ color: '#dbeafe' }}>18K:</span>
                <span style={{ color: '#ffe500', fontWeight: 700 }}>
                  {formatINR(rate18K)}/g
                </span>
              </div>

              {/* Silver */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <span style={{ color: '#dbeafe' }}>Silver:</span>
                <span style={{ color: '#ffffff', fontWeight: 700 }}>
                  ₹{rateSilver}/g
                </span>
              </div>

              {/* Trend */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                color: '#76ff03',
                fontSize: '0.7rem'
              }}>
                <TrendingUp size={12} /> {liveGoldRates?.change || liveGoldRates?.rateChange || '+0.42%'}
              </div>

              {/* BIS Hallmark */}
              <div className="bis-badge-ticker" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                color: '#ffe500',
                fontWeight: 700,
                fontSize: '0.7rem'
              }}>
                <ShieldCheck size={13} color="#ffe500" />
                <span>BIS 916 Assayed</span>
              </div>

              {/* View Live Rates & Calculator Button */}
              <button
                onClick={() => navigate('/live-rates')}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.35)',
                  color: '#ffffff',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  padding: '0.12rem 0.5rem',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '2px',
                  transition: 'background 0.15s ease'
                }}
              >
                <span>Live Rates</span>
                <ChevronRight size={11} />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ticker-inner-wrap {
          display: flex;
          align-items: center;
          justifyContent: space-between;
          gap: 1.2rem;
          white-space: nowrap;
          overflow: hidden;
        }
        .ticker-announcement-col {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 0;
          flex: 1;
          overflow: hidden;
        }
        .ticker-notice-text {
          color: #fff;
          font-weight: 500;
          font-size: 0.76rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ticker-rates-col {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 600;
          flex-shrink: 0;
          background: rgba(212, 175, 55, 0.08);
          padding: 0.15rem 0.6rem;
          border-radius: 999px;
          border: 1px solid rgba(212, 175, 55, 0.2);
        }

        @media (max-width: 768px) {
          .gold-rate-ticker-ribbon {
            padding: 0.25rem 0 !important;
            overflow: hidden !important;
          }
          .ticker-inner-wrap {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 0.5rem !important;
            white-space: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            scrollbar-width: none !important;
          }
          .ticker-inner-wrap::-webkit-scrollbar {
            display: none !important;
          }
          .ticker-announcement-col {
            display: none !important;
          }
          .ticker-rates-col {
            display: inline-flex !important;
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
            gap: 0.6rem !important;
            font-size: 0.71rem !important;
            flex-wrap: nowrap !important;
          }
          .bis-badge-ticker {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
